import { platformBrowser } from '@angular/platform-browser';
import * as Sentry from '@sentry/angular';

import { AppModule } from './app/app.module';
import { registerLicense } from '@syncfusion/ej2-base';
import { environment } from './app/environments/environment';
// Registering Syncfusion license key
// registerLicense('Ngo9BigBOggjHTQxAR8/V1NDaF5cWWtCf1FpRmJGdld5fUVHYVZUTXxaS00DNHVRdkdnWH5ccXRTRmZfWEx/XkA=');
registerLicense('Ngo9BigBOggjHTQxAR8/V1JAaF5cX2pCd1p/TH5YfUNzdUVEY1ZUTXxaS1ZhSXxVdkJhWX9ecnZXQGNcWU19XEY=');

// ── PII patterns ────────────────────────────────────────────────────────────
const EMAIL_RE = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
// Matches "John Smith", "Mary-Jane O'Brien", "firstname lastname" (2–4 words)
const NAME_RE = /\b([A-Z][a-z'-]+)(\s[A-Z][a-z'-]+){1,3}\b/g;

/** Recursively scrub PII from any string leaves in an object/array/string. */
function scrubPii(value: unknown): unknown {
  if (typeof value === 'string') {
    return value
      .replace(EMAIL_RE, '[email]')
      .replace(NAME_RE, '[name]');
  }
  if (Array.isArray(value)) {
    return value.map(scrubPii);
  }
  if (value !== null && typeof value === 'object') {
    const result: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      result[k] = scrubPii(v);
    }
    return result;
  }
  return value;
}

/** Strip known PII fields and run regex scrubbing on the whole event. */
function stripPii(event: Sentry.ErrorEvent): Sentry.ErrorEvent {
  // 1. Clear the built-in user fields Sentry collects automatically
  if (event.user) {
    event.user = {
      id: event.user.id,          // keep anonymous numeric/UUID id for issue grouping
      // deliberately omit: email, username, ip_address, name
    };
  }

  // 2. Scrub request headers that can carry identity tokens / cookies
  if (event.request?.headers) {
    const safe: Record<string, string> = {};
    const BLOCKED = new Set(['cookie', 'authorization', 'x-auth-token', 'x-api-key']);
    for (const [k, v] of Object.entries(event.request.headers)) {
      safe[k] = BLOCKED.has(k.toLowerCase()) ? '[filtered]' : v;
    }
    event.request.headers = safe;
  }

  // 3. Scrub query string from request URL
  if (event.request?.url) {
    try {
      const u = new URL(event.request.url);
      u.search = '';
      event.request.url = u.toString();
    } catch { /* not a full URL, leave as-is */ }
  }

  // 4. Regex-scrub all string leaves: exception messages, breadcrumbs, extras
  event.exception = scrubPii(event.exception) as typeof event.exception;
  event.breadcrumbs = scrubPii(event.breadcrumbs) as typeof event.breadcrumbs;
  event.extra = scrubPii(event.extra) as typeof event.extra;
  event.tags = scrubPii(event.tags) as typeof event.tags;

  return event;
}

if (environment.sentryEnabled) {
  Sentry.init({
    dsn: environment.sentryDsn,
    environment: environment.sentryEnvironment,
    release: environment.sentryRelease,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }),
      Sentry.breadcrumbsIntegration({
        console: true,      // console.log / warn / error calls
        dom: true,          // click, input, and other DOM interactions
        fetch: true,        // fetch() requests
        xhr: true,          // XMLHttpRequest calls
        history: true,      // navigation / history pushState
        sentry: true,       // Sentry's own log calls
      }),
    ],
    // Keep the last 100 breadcrumbs before each error
    maxBreadcrumbs: 100,
    tracesSampleRate: 0.2,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    // WEB-PROD-C7: Syncfusion RichTextEditor defect. handleSelectionChange() (ej2-richtexteditor rich-text-editor.js:3859) dereferences
    // quickToolbarModule.linkQTBar without the isNOU() guard used elsewhere. It is triggered when toolbarSettings.enable is false
    // (quick toolbar module never created, e.g. the Actions comment editor) and the user drag-selects text and releases outside the editor.
    // No app behavior depends on the skipped code. Harmless, no upstream fix available.
    ignoreErrors: [
      /Cannot read propert(?:y|ies) of undefined \(reading 'linkQTBar'\)/,  // Chrome/Edge
      /quickToolbarModule\.linkQTBar/,                                     // Safari
      /quickToolbarModule is undefined/,                                   // Firefox
    ],
    beforeSend(event) {
      try {
        return stripPii(event) as Sentry.ErrorEvent;
      } catch (e) {
        console.error('[Sentry] beforeSend error:', e);
        return event; // send unstripped rather than drop
      }
    },
  });
}

window.addEventListener('unhandledrejection', (event) => {
  const msg: string = (event.reason as Error)?.message ?? '';

  if (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed')
  ) {
    const alreadyReloaded = sessionStorage.getItem('chunk-reload');

    if (!alreadyReloaded) {
      sessionStorage.setItem('chunk-reload', '1');
      window.location.reload();
    }
  }
});

platformBrowser()
  .bootstrapModule(AppModule)
  .then(() => sessionStorage.removeItem('chunk-reload'))
  .catch((err) => console.error(err));

  